import { Star, Users, Crown, Trophy, Sun, Lock } from 'lucide-react';
import { Text } from '@/components/ui/index.js';
import styles from './AchievementCard.module.css';

// Map achievement icons to components
const iconMap = {
  star: Star,
  users: Users,
  crown: Crown,
  trophy: Trophy,
  sun: Sun,
};

/**
 * AchievementCard - Achievement badge with locked/unlocked states
 * 
 * Features:
 * - Icon with sparkle effect for unlocked achievements
 * - Locked state with grayscale and lock icon
 * - Progress indicator for in-progress achievements
 * - Hover effects
 */
export function AchievementCard({ achievement, delay = 0 }) {
  const {
    name,
    description,
    unlocked,
    progress,
    total,
    icon,
  } = achievement;

  const Icon = iconMap[icon] || Star;
  const showProgress = !unlocked && progress !== undefined && total !== undefined;
  const progressPercent = showProgress ? (progress / total) * 100 : 0;

  return (
    <div
      className={`${styles.card} ${unlocked ? styles.unlocked : styles.locked}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Icon Container */}
      <div className={styles.iconContainer}>
        <Icon size={28} className={styles.icon} />
        {unlocked && (
          <>
            <span className={styles.sparkle1}>✦</span>
            <span className={styles.sparkle2}>✦</span>
          </>
        )}
        {!unlocked && <Lock size={14} className={styles.lockIcon} />}
      </div>

      {/* Achievement Info */}
      <div className={styles.info}>
        <Text size="sm" weight="semibold" color="primary" className={styles.name}>
          {name}
        </Text>
        <Text size="xs" color="muted" className={styles.description}>
          {description}
        </Text>
      </div>

      {/* Progress Bar (for locked achievements with progress) */}
      {showProgress && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <Text size="xs" color="muted" className={styles.progressText}>
            {progress}/{total}
          </Text>
        </div>
      )}

      {/* Unlocked Indicator */}
      {unlocked && (
        <div className={styles.unlockedBadge}>
          <Star size={10} fill="currentColor" />
        </div>
      )}
    </div>
  );
}

export default AchievementCard;
